import { type ErrorInfo, type FC, StrictMode } from 'react'
import { BrowserRouter } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'

import { isNative } from '@dhl/webview'
import { ApiGatewayProvider } from '@dhl/api-gateway-provider'
import { MapProvider } from '@dhl/google-maps'
import { dataLayerPublish } from '@dhl/datalayer'
import { I18nProvider, useSetLanguageDocument } from '@dhl/i18n'
import { useSetupLocalXsrfRequestInterceptor } from '@dhl/receiver-axios'
import { initDataLayer } from '@dhl/third-party'
import { CacheProvider, ThemeProvider } from '@dhl-ui/connector'
import { VersionProvider } from '@dhl/version-provider'
import { useMobileViewportHeight } from '@dhl/viewport-height'
import { SnackbarProvider } from '@dhl-ui/components'
import { useDeviceDetect } from '@dhl/theme'

import { getConfig } from '~/config/index.js'
import { PortalNativeBridge } from '~/PortalNativeBridge.js'
import { queryClient } from '~/query-client.js'
import { Routes } from '~/Routes.js'
import { AppProvider, LocationHistoryProvider } from './context/index.js'
import { StartUp } from '~/StartUp.js'
import { ErrorBoundary } from './components-page/ErrorBoundary.js'
import { PortalShellPage } from './components-page/Page/PortalShellPage.js'

initDataLayer()

const { apiGatewayHost, basePath, cspNonce, maps, version } = getConfig()

const baseName = basePath.slice(0, -1)

export const App: FC = () => {
  const isNativeApp = isNative()
  const { isMobile } = useDeviceDetect()

  useSetLanguageDocument()
  useMobileViewportHeight()
  useSetupLocalXsrfRequestInterceptor(process.env.REACT_APP_ENVIRONMENT)

  const handleError = (error: Error, errorInfo: ErrorInfo) => {
    dataLayerPublish('ERROR', { id: 'error-boundary', error, errorInfo })
  }

  return (
    <CacheProvider nonce={cspNonce}>
      <ThemeProvider
        options={{
          variant: isMobile ? 'mobile' : 'desktop',
          disablePtr: true,
          disableActiveState: isNativeApp,
          userSelect: isNativeApp ? 'none' : undefined,
        }}
      >
        <ApiGatewayProvider host={apiGatewayHost}>
          <ErrorBoundary PageComponent={PortalShellPage} onError={handleError}>
            <QueryClientProvider client={queryClient}>
              <MapProvider apiKey={maps.apiKey} nonce={cspNonce}>
                <SnackbarProvider autoHideDuration={3000}>
                  <I18nProvider>
                    <AppProvider>
                      <BrowserRouter
                        basename={baseName}
                        future={{
                          v7_relativeSplatPath: true,
                          v7_startTransition: true,
                        }}
                      >
                        <LocationHistoryProvider>
                          <VersionProvider url={`${baseName}/version.json`} version={version}>
                            <PortalNativeBridge>
                              <StartUp>
                                <StrictMode>
                                  <Routes />
                                </StrictMode>
                              </StartUp>
                            </PortalNativeBridge>
                          </VersionProvider>
                        </LocationHistoryProvider>
                      </BrowserRouter>
                    </AppProvider>
                  </I18nProvider>
                </SnackbarProvider>
              </MapProvider>
            </QueryClientProvider>
          </ErrorBoundary>
        </ApiGatewayProvider>
      </ThemeProvider>
    </CacheProvider>
  )
}
